import React from 'react';

import IconArrowDown from '@mui/icons-material/ArrowDropDown';
import IconArrowUp from '@mui/icons-material/ArrowDropUp';
import { CardHeader, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { common } from '@mui/material/colors';
import Typography from '@mui/material/Typography';

import { TablePanelRow } from './TablePanelRow';

import NumericalPagingBar from '../bar/NumericalPagingBar';
import NumericalServerPagingBar from '../bar/NumericalServerPagingBar';
import Utils from '../utils/Utils';

const black = common.black;

export default class TablePanel extends React.Component {
    static defaultStyles = {
        fontSize: '12px'
    };

    static cellStyleWide = {
        overflow: 'initial',
        textOverflow: 'initial',
        whiteSpace: 'normal',
        wordBreak: 'break-word'
    };

    static cellStyleCenter = {
        textAlign: 'center'
    };

    state = {
        currentPage: 1,
        rowsOnPage: 10,
        sortField: '',
        sortDesc: false
    };

    static defaultProps = {
        data: [],
        rowHoverable: true
    };

    componentWillMount() {
        const { showAll } = this.props;
        showAll && this.setState({ showAll });
    }

    componentWillReceiveProps(newProps) {
        const { rowsOnPage } = this.props;

        if (rowsOnPage !== newProps.rowsOnPage) {
            this.setState({ rowsOnPage: newProps.rowsOnPage });
        }
    }

    onHeaderClick(fieldConfig) {
        if (fieldConfig.hasOwnProperty('sortField') || typeof fieldConfig.field === 'string') {
            this.setState(
                { sortDesc: !this.state.sortDesc, sortField: fieldConfig.sortField || fieldConfig.field },
                () => {
                    this.props.onDataSort && this.props.onDataSort(this.state.sortField, this.state.sortDesc);
                }
            );
        }
    }

    getToolbar = () =>
        this.props.toolbar || (
            <CardHeader
                style={this.props.toolbarStyle}
                title={this.props.toolbarTitle || <Typography variant="body2">{this.props.toolbarText}</Typography>}
                subheader={
                    this.props.toolbarSubTitle || (
                        <Typography variant="body2">
                            count <b color={black}>{this.props.data.length}</b>
                        </Typography>
                    )
                }
            />
        );

    onPageNumberChange = (currentPage) =>
        this.setState(
            { currentPage },
            () => this.props.onPageNumberChange && this.props.onPageNumberChange(currentPage)
        );

    onRowsOnPageChange = (rowsOnPage, currentPage) =>
        this.setState(
            { currentPage },
            () => this.props.onRowsOnPageChange && this.props.onRowsOnPageChange(rowsOnPage, currentPage)
        );

    getSortIcon = (c) => {
        return this.state.sortDesc ? <IconArrowUp /> : <IconArrowDown />;
    };

    render() {
        let {
            data,
            activeFilters,
            hideToolbar,
            onRowClick,
            onCellClick,
            size,
            tableSize,
            isLoading,
            hidePaging,
            headerColumnStyle,
            onHeaderClick,
            onDataSort,
            collapsibleConfig,
            rowColumnStyle,
            rowHoverable,
            rowClasses,
            config,
            hideHeader,
            tableClassName,
            serverPaging,
            tableWrapperStyle,
            emptyMessage,
            showScroll
        } = this.props;
        const { rowsOnPage, currentPage, sortField, showAll } = this.state;

        /**
         * Filter data
         */
        if (activeFilters && !serverPaging) {
            activeFilters.forEach((filter) => {
                const fr = new RegExp(filter.filterValue);
                data = data.filter((item) => fr.test(item[filter.filterName]));
            });
        }

        /**
         * Sort data
         */
        if (sortField && !onDataSort) {
            data =
                typeof sortField === 'function'
                    ? sortField(data)
                    : data.sort((a, b) => Utils.sortByNumberOrString(a[sortField], b[sortField]));
            this.state.sortDesc && (data = data.reverse());
        }

        /**
         * Count of items to show in grid for local paging
         */
        if (!serverPaging) {
            const totalRowsOnPage = showAll ? 9999 : rowsOnPage;
            const to = totalRowsOnPage * currentPage;
            const from = to - totalRowsOnPage;

            data = data.slice(from, to);
        }

        const rootClasses = ['vulners-table-panel'];
        !showScroll && rootClasses.push('vulners-hide-scroll');

        return (
            <div className={tableClassName}>
                {hideToolbar ? '' : this.getToolbar()}

                <div
                    className={rootClasses.join(' ')}
                    style={Object.assign({ overflowX: 'scroll', scrollbarWidth: 'none' }, tableWrapperStyle)}
                >
                    <Table size={tableSize || 'small'}>
                        {!hideHeader && (
                            <TableHead>
                                <TableRow>
                                    {collapsibleConfig && <TableCell variant="head" />}
                                    {config.map((c, i) => (
                                        <TableCell
                                            key={'thead' + i}
                                            variant="head"
                                            onClick={() => (onHeaderClick ? onHeaderClick(c) : this.onHeaderClick(c))}
                                            style={Object.assign(
                                                { maxWidth: c.width, cursor: 'pointer', fontWeight: 600 },
                                                headerColumnStyle,
                                                c.headerStyle
                                            )}
                                        >
                                            <span className="vulners-table-sort">
                                                {c.name}
                                                {(c.sortField === this.state.sortField ||
                                                    c.field === this.state.sortField) &&
                                                    this.getSortIcon(c)}
                                            </span>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                        )}
                        <TableBody>
                            {data.map((item, i) => (
                                <TablePanelRow
                                    key={'tbody' + i}
                                    data={item}
                                    collapsibleData={item.scan_info}
                                    config={config}
                                    rowClasses={rowClasses}
                                    rowColumnStyle={rowColumnStyle}
                                    defaultStyles={this.defaultStyles}
                                    collapsibleConfig={collapsibleConfig}
                                    hover={rowHoverable}
                                    onClick={(e) => {
                                        // Ignore buttons and icons on row
                                        if (
                                            e.target.nodeName !== 'button' &&
                                            e.target.nodeName !== 'svg' &&
                                            e.target.nodeName !== 'path'
                                        ) {
                                            onRowClick && onRowClick(item, e);
                                        }
                                    }}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </div>

                {!hidePaging &&
                    (serverPaging ? (
                        <NumericalServerPagingBar {...this.props} />
                    ) : (
                        (this.props.data.totalCount || this.props.data.length) > 10 && (
                            <NumericalPagingBar
                                showAll={this.state.showAll}
                                rowsOnPage={rowsOnPage}
                                totalRows={this.props.data.totalCount || this.props.data.length}
                                onPageNumberChange={(currentPage) => this.setState({ currentPage })}
                                onRowsOnPageChange={(rowsOnPage, currentPage) =>
                                    this.setState({ rowsOnPage, currentPage, showAll: false })
                                }
                            />
                        )
                    ))}
            </div>
        );
    }
}
